/**
 * CMRP multi-row course builder.
 *
 * - Shared Campus/Term row sits above course lines (editable once per session).
 * - Course #1 reveals only after Campus (when shown) + Term are selected.
 * - Auto-appends an empty row when a course line’s Section is selected (max 30),
 *   except when every visible DDCS field on that row has only one displayable value.
 * - Non-last rows: Delete removes the line. Last (or only) row: Clear resets
 *   fields in place; the control is hidden while the row is empty or only
 *   singleton auto-selects.
 * - View Materials is hidden until ≥1 complete course line; label shows count.
 *
 * FOLLOW-UP: Confirm with Dev whether Discover/Shop/Access multi-campus integration
 * will allow multiple campus/term values per session. This module currently enforces
 * a single campus/term for all rows (session constraint).
 */
(function (window) {
  'use strict';

  /** Confirmed with product: max supported course rows. */
  const MAX_ROWS = 30;

  function cmrpId(base, scope) {
    if (typeof window.cmrpId === 'function') return window.cmrpId(base, scope);
    if (!scope || scope === 'activeCt') return base;
    return `${base}-${scope}`;
  }

  function stampOption(option, fullText) {
    if (typeof window.cmrpStampOptionFullLabel === 'function') {
      window.cmrpStampOptionFullLabel(option, fullText);
      return;
    }
    option.textContent = fullText;
    option.dataset.cmrpFullLabel = fullText;
  }

  function fullLabel(select) {
    if (typeof window.cmrpSelectSelectedFullLabel === 'function') {
      return window.cmrpSelectSelectedFullLabel(select);
    }
    const opt = select?.options?.[select.selectedIndex];
    return (opt?.dataset?.cmrpFullLabel || opt?.textContent || '').trim();
  }

  function decodeEntities(text) {
    if (typeof window.decodeCmrpHtmlEntities === 'function') {
      return window.decodeCmrpHtmlEntities(text);
    }
    const el = document.createElement('textarea');
    el.innerHTML = String(text ?? '');
    return el.value;
  }

  function normalize(value) {
    return String(value ?? '')
      .toLowerCase()
      .replace(/\s+/g, '');
  }

  class CmrpCourseRowManager {
    constructor() {
      this.rowsRoot = null;
      this.template = null;
      this.viewBtn = null;
      this.addBtn = null;
      this.suffix = '';
      this.maxRows = MAX_ROWS;
      this._bound = false;
    }

    init() {
      const form = document.querySelector(
        '.cmrp-filter-form--course-builder'
      );
      if (!form) return;

      this.suffix = form.getAttribute('data-cmrp-form-suffix') || '';
      const rowsId = this.suffix
        ? `cmrp-course-rows-${this.suffix}`
        : 'cmrp-course-rows';
      const tplId = this.suffix
        ? `cmrp-course-row-template-${this.suffix}`
        : 'cmrp-course-row-template';
      const viewId = this.suffix
        ? `view-materials-${this.suffix}`
        : 'view-materials';

      this.rowsRoot = document.getElementById(rowsId);
      this.template = document.getElementById(tplId);
      this.viewBtn = document.getElementById(viewId);
      // Manual "+ Add Course" removed — rows auto-append on section select.
      this.addBtn = null;

      if (!this.rowsRoot) return;

      const maxAttr = Number(this.rowsRoot.getAttribute('data-cmrp-max-rows'));
      if (Number.isFinite(maxAttr) && maxAttr > 0) {
        this.maxRows = Math.min(maxAttr, MAX_ROWS);
      }

      this.bindEvents();
      this.bindPrimaryDelete();
      this.relabelRows();
      this.syncCourseRowsVisibility();
      this.updateViewMaterialsButton();
      this.updateAddCourseButton();
    }

    bindEvents() {
      if (this._bound) return;
      this._bound = true;

      this.viewBtn?.addEventListener('click', e => {
        e.preventDefault();
        void this.handleViewMaterials();
      });

      // Term/campus change clears extra rows (shared session constraint)
      document.addEventListener('change', e => {
        const t = e.target;
        if (!(t instanceof HTMLSelectElement)) return;
        if (
          t.id === cmrpId('term', 'cb') ||
          t.id === cmrpId('campus', 'cb')
        ) {
          this.resetExtraRows();
          this.syncCourseRowsVisibility();
          this.updateViewMaterialsButton();
        }
      });

      document.addEventListener('click', e => {
        const btn = e.target?.closest?.('[data-cmrp-clear-row]');
        if (!btn || !this.rowsRoot?.contains(btn)) return;
        e.preventDefault();
        const row = btn.closest('.cmrp-course-row');
        if (row) this.handleRowDeleteOrClear(row);
      });
    }

    bindPrimaryDelete() {
      const primary = this.getPrimaryRow();
      if (!primary) return;
      const btn = primary.querySelector('[data-cmrp-clear-row]');
      if (btn && !btn.dataset.cmrpClearBound) {
        btn.dataset.cmrpClearBound = '1';
      }
    }


    getRows() {
      if (!this.rowsRoot) return [];
      return Array.from(
        this.rowsRoot.querySelectorAll(':scope > .cmrp-course-row')
      );
    }

    getPrimaryRow() {
      return this.rowsRoot?.querySelector(
        '.cmrp-course-row[data-cmrp-row="primary"]'
      );
    }

    fieldInRow(row, field) {
      return row?.querySelector(`select[data-cmrp-field="${field}"]`);
    }

    showDivision() {
      const sidebar = window.courseMaterialsSidebar;
      if (sidebar && typeof sidebar.show_division === 'boolean') {
        return sidebar.show_division;
      }
      const primaryDiv = document.getElementById(cmrpId('division_field', 'cb'));
      return Boolean(primaryDiv && !primaryDiv.hidden && primaryDiv.style.display !== 'none');
    }

    sharedCampusTerm() {
      const campusEl =
        document.getElementById(cmrpId('campus', 'cb')) ||
        window.cmrpCourseBuilderCampusTermEl?.('campus');
      const termEl =
        document.getElementById(cmrpId('term', 'cb')) ||
        window.cmrpCourseBuilderCampusTermEl?.('term');
      const termLabel = fullLabel(termEl);
      const campusLabel = fullLabel(campusEl) || campusEl?.value || '';
      return {
        campus: campusEl?.value || '',
        campusLabel:
          campusLabel && campusLabel !== 'Campus' ? campusLabel : '',
        termId: termEl?.value || '',
        termLabel:
          termLabel && termLabel !== 'Term' ? termLabel : termEl?.value || '',
      };
    }

    /**
     * True when the shared Campus control is shown (hidden for single-campus stores).
     */
    isCampusVisibleOnPrimary() {
      const form = document.querySelector('.cmrp-filter-form--course-builder');
      const campusGroup =
        form?.querySelector(
          '.cmrp-campus-term-row [data-cmrp-shared-field="campus"]'
        ) ||
        form?.querySelector(
          '#campus-term-dropdowns [data-cmrp-shared-field="campus"], #campus-term-dropdowns-cb [data-cmrp-shared-field="campus"]'
        ) ||
        document
          .getElementById(cmrpId('campus', 'cb'))
          ?.closest(
            '[data-cmrp-shared-field="campus"], .cmrp-cb-item--campus-term, .form-group'
          );
      if (!campusGroup) return false;
      if (campusGroup.hidden) return false;
      const style = window.getComputedStyle(campusGroup);
      if (style.display === 'none' || style.visibility === 'hidden') {
        return false;
      }
      // Inline hide used by single-campus populateCampus
      if (campusGroup.style.display === 'none') return false;
      return true;
    }

    /**
     * Campus (when visible) + Term must both be set before course lines appear.
     */
    hasSharedCampusTerm() {
      const { campus, termId } = this.sharedCampusTerm();
      const needsCampus = this.isCampusVisibleOnPrimary();
      if (needsCampus && !campus) return false;
      return Boolean(termId);
    }

    /**
     * Reveal Course #1 (and rows root) only after shared Campus/Term are ready.
     */
    /**
     * Show Division only when this campus uses it. Course #1 is always in the
     * DOM; URL restore / SID un-hide must not bring the column back when unused.
     */
    syncDivisionFieldVisibility(row) {
      const needsDivision = this.showDivision();
      const rows = row ? [row] : this.getRows();
      rows.forEach(current => {
        const divWrap = current?.querySelector('[data-cmrp-field-wrap="division"]');
        if (!divWrap) return;
        divWrap.hidden = !needsDivision;
        divWrap.style.display = needsDivision ? '' : 'none';
      });
    }

    syncCourseRowsVisibility() {
      const ready = this.hasSharedCampusTerm();
      if (this.rowsRoot) {
        this.rowsRoot.hidden = !ready;
      }
      const primary = this.getPrimaryRow();
      if (primary) {
        primary.hidden = !ready;
      }
      if (ready) {
        // Clear any display:none left by hideCourseSelectionFields / SID mode.
        // Skip Division — unused campuses keep that wrap hidden.
        this.rowsRoot
          ?.querySelectorAll('.cmrp-course-line-fields')
          .forEach(el => {
            if (el.getAttribute('data-cmrp-field-wrap') === 'division') return;
            if (el.style.display === 'none') el.style.display = '';
          });
        this.syncDivisionFieldVisibility();
      } else {
        this.resetExtraRows();
      }
      this.updateViewMaterialsButton();
    }

    /** Hook from Liquid campus/term onchange. */
    onSharedCampusTermChange() {
      queueMicrotask(() => {
        const restoring =
          window.courseMaterialsPageInstance?.components?.urlManager?.isUrlRestorePending?.();
        if (!restoring) {
          this.resetExtraRows();
        }
        this.syncCourseRowsVisibility();
        this.updateViewMaterialsButton();
      });
    }

    setLockFieldVisible(el, visible) {
      if (!el) return;
      el.hidden = !visible;
      el.style.display = visible ? '' : 'none';
    }

    isRowComplete(row) {
      if (!row) return false;
      if (!this.hasSharedCampusTerm()) return false;

      const needsDivision = this.showDivision();
      const division = this.fieldInRow(row, 'division');
      const department = this.fieldInRow(row, 'department');
      const course = this.fieldInRow(row, 'course');
      const section = this.fieldInRow(row, 'section');

      if (needsDivision && !division?.value) return false;
      return Boolean(department?.value && course?.value && section?.value);
    }

    countCompleteRows() {
      return this.getRows().filter(row => this.isRowComplete(row)).length;
    }

    relabelRows() {
      this.getRows().forEach((row, i) => {
        row.setAttribute('data-cmrp-row-index', String(i));
        const label = row.querySelector('.cmrp-course-row__label');
        const numEl = row.querySelector('[data-cmrp-row-number]');
        if (numEl) {
          numEl.textContent = String(i + 1);
        } else if (label) {
          label.textContent = `Course #${i + 1}`;
        }
      });
    }

    updateViewMaterialsButton() {
      this.syncDeleteButtons();
      if (!this.viewBtn) return;
      const n = this.countCompleteRows();
      const textEl =
        this.viewBtn.querySelector('.buy-by-course-builder__view-materials__text') ||
        this.viewBtn;
      const label =
        n === 0
          ? 'View Materials'
          : n === 1
            ? 'View Materials (1 Course)'
            : `View Materials (${n} Courses)`;
      textEl.textContent = label;
      this.viewBtn.disabled = n === 0;
      this.viewBtn.classList.toggle('disabled', n === 0);
      // Hide until at least one course line is complete
      this.viewBtn.hidden = n === 0;
      const footer = this.viewBtn.closest('.cmrp-course-rows__footer');
      if (footer) footer.hidden = n === 0;
    }

    isLastRow(row) {
      const rows = this.getRows();
      return rows.length > 0 && rows[rows.length - 1] === row;
    }

    /**
     * Empty fields, or values that were auto-selected because that field has
     * only one option, count as a cleared row (no Delete/Clear control).
     */
    isRowEffectivelyCleared(row) {
      if (!row) return true;
      const fields = ['division', 'department', 'course', 'section'];
      const active = fields.filter(field => this.isDdcsFieldActive(row, field));
      if (!active.length) return true;
      return active.every(field => {
        const select = this.fieldInRow(row, field);
        const val = String(select?.value || '').trim();
        if (!val) return true;
        return this.selectRealOptionCount(select) <= 1;
      });
    }

    syncDeleteButtons() {
      const rows = this.getRows();
      const onlyOneRow = rows.length <= 1;
      rows.forEach((row, i) => {
        const btn = row.querySelector('[data-cmrp-clear-row]');
        if (!btn) return;
        const isLast = i === rows.length - 1;
        const hide = this.isRowEffectivelyCleared(row);
        // One row (or a singleton-only row that never appends another): drop the
        // control from layout. Reserved spacer is only for aligning later rows.
        const collapse =
          hide && (onlyOneRow || this.rowHasOnlySingleDdcsChoices(row));
        btn.removeAttribute('hidden');
        btn.classList.toggle('cmrp-course-row__delete--collapsed', collapse);
        btn.classList.toggle(
          'cmrp-course-row__delete--reserved',
          hide && !collapse
        );
        btn.disabled = hide;
        btn.setAttribute('aria-hidden', hide ? 'true' : 'false');
        btn.style.visibility = '';
        btn.style.pointerEvents = '';
        const labelEl = btn.querySelector('span');
        const courseNum = i + 1;
        if (isLast) {
          if (labelEl) labelEl.textContent = 'Clear';
          btn.setAttribute(
            'aria-label',
            `Clear Course #${courseNum} selections`
          );
        } else {
          if (labelEl) labelEl.textContent = 'Delete';
          btn.setAttribute('aria-label', `Delete Course #${courseNum}`);
        }
      });
    }

    handleRowDeleteOrClear(row) {
      if (!row) return;
      if (this.isLastRow(row) || this.getRows().length <= 1) {
        this.clearRowFields(row);
        return;
      }
      this.removeCourseRow(row);
    }

    /**
     * Remove a non-last course line. Primary keeps its DOM/IDs: the next row’s
     * selections move onto Course #1, then that extra node is dropped.
     */
    removeCourseRow(row) {
      if (!row) return;
      const rows = this.getRows();
      if (rows.length <= 1 || this.isLastRow(row)) {
        this.clearRowFields(row);
        return;
      }

      const primary = this.getPrimaryRow();
      if (row === primary) {
        const next = rows[1];
        if (next) {
          this.transferRowSelections(next, primary);
          next.remove();
        }
      } else {
        row.remove();
      }

      window.cmrpCustomDropdowns?.cleanupDetached?.();
      this.relabelRows();
      this.updateViewMaterialsButton();
    }

    transferRowSelections(from, to) {
      if (!from || !to) return;
      const needsDivision = this.showDivision();
      const fromDiv = this.fieldInRow(from, 'division')?.value || '';
      const fromDept = this.fieldInRow(from, 'department')?.value || '';
      const fromCourse = this.fieldInRow(from, 'course')?.value || '';
      const fromSection = this.fieldInRow(from, 'section')?.value || '';

      this._suppressAutoAppend = true;
      try {
        const division = this.fieldInRow(to, 'division');
        const department = this.fieldInRow(to, 'department');
        const course = this.fieldInRow(to, 'course');
        const section = this.fieldInRow(to, 'section');
        this.syncDivisionFieldVisibility(to);

        if (needsDivision) {
          this.populateDivisionOptions(division, { autoSelect: false });
          if (fromDiv && division) division.value = fromDiv;
          this.populateDepartmentOptions(department, fromDiv, {
            autoSelect: false,
          });
        } else {
          this.populateDepartmentOptions(department, '', { autoSelect: false });
        }
        if (fromDept && department) department.value = fromDept;
        this.populateCourseOptions(course, fromDept, { autoSelect: false });
        if (fromCourse && course) course.value = fromCourse;
        this.populateSectionOptions(section, fromCourse, { autoSelect: false });
        if (fromSection && section) section.value = fromSection;
        this.enhanceCustomDropdowns(to);
      } finally {
        this._suppressAutoAppend = false;
      }
    }

    updateAddCourseButton() {
      if (!this.addBtn) return;
      const atMax = this.getRows().length >= this.maxRows;
      this.addBtn.disabled = atMax;
      this.addBtn.classList.toggle('disabled', atMax);
      this.addBtn.hidden = false;
    }

    /**
     * Called from primary-row cascade onchange hooks in Liquid.
     * @param {'division'|'department'|'course'|'section'} field
     */
    onPrimaryCascadeChange(field) {
      const primary = this.getPrimaryRow();
      if (!primary) return;

      this.syncLockedTermDisplays();

      // Only append once Section is set (row fully complete) — never on course alone
      if (field === 'section') {
        queueMicrotask(() => {
          this.maybeAutoAppendRow(primary);
          this.updateViewMaterialsButton();
        });
      }

      this.updateViewMaterialsButton();
    }

    /**
     * After sidebar finishes populating sections (incl. auto-select).
     */
    onPrimarySectionsPopulated() {
      const primary = this.getPrimaryRow();
      this.syncLockedTermDisplays();
      queueMicrotask(() => {
        this.maybeAutoAppendRow(primary);
        this.updateViewMaterialsButton();
      });
      this.updateViewMaterialsButton();
    }

    maybeAutoAppendRow(row) {
      if (this._suppressAutoAppend) return;
      // Do not append during URL deep-link restore (form cascade + hydrate race).
      if (window.courseMaterialsPageInstance?.components?.urlManager?.isUrlRestorePending?.()) {
        return;
      }
      if (!row || !this.isRowComplete(row)) return;
      const rows = this.getRows();
      const isLast = rows[rows.length - 1] === row;
      if (!isLast) return;
      if (rows.length >= this.maxRows) return;
      // A follow-up row would only repeat the same single DDCS combination.
      if (this.rowHasOnlySingleDdcsChoices(row)) return;
      this.appendEmptyRow();
    }

    selectRealOptionCount(select) {
      if (!select?.options) return 0;
      return Array.from(select.options).filter(
        opt => String(opt.value || '').trim() !== ''
      ).length;
    }

    isDdcsFieldActive(row, field) {
      if (field === 'division' && !this.showDivision()) return false;
      const wrap = row?.querySelector(`[data-cmrp-field-wrap="${field}"]`);
      if (wrap && (wrap.hidden || wrap.style.display === 'none')) return false;
      return Boolean(this.fieldInRow(row, field));
    }

    /** True when every visible DDCS dropdown on the row has exactly one real option. */
    rowHasOnlySingleDdcsChoices(row) {
      const fields = ['division', 'department', 'course', 'section'];
      const active = fields.filter(field => this.isDdcsFieldActive(row, field));
      if (!active.length) return false;
      return active.every(
        field => this.selectRealOptionCount(this.fieldInRow(row, field)) === 1
      );
    }

    handleManualAddCourse() {
      const rows = this.getRows();
      if (rows.length >= this.maxRows) return;

      const last = rows[rows.length - 1];
      // If last row is still empty, focus it instead of stacking empties
      if (last && !this.isRowComplete(last) && this.isRowEmpty(last)) {
        this.fieldInRow(last, 'department')?.focus?.();
        return;
      }
      this.appendEmptyRow();
    }

    isRowEmpty(row) {
      const division = this.fieldInRow(row, 'division');
      const department = this.fieldInRow(row, 'department');
      const course = this.fieldInRow(row, 'course');
      const section = this.fieldInRow(row, 'section');
      return (
        !division?.value &&
        !department?.value &&
        !course?.value &&
        !section?.value
      );
    }

    appendEmptyRow() {
      if (!this.template || !this.rowsRoot) return null;
      if (this.getRows().length >= this.maxRows) return null;

      const frag = this.template.content.cloneNode(true);
      const row = frag.querySelector('.cmrp-course-row');
      if (!row) return null;

      this.rowsRoot.appendChild(frag);
      const appended = this.rowsRoot.lastElementChild;
      this.setupExtraRow(appended);
      this.relabelRows();
      this.syncLockedTermDisplays();
      // Leon CMS-195 custom dropdowns: enhance newly cloned row controls
      this.enhanceCustomDropdowns(appended);
      this.updateAddCourseButton();
      this.updateViewMaterialsButton();
      return appended;
    }

    enhanceCustomDropdowns(root) {
      window.cmrpCustomDropdowns?.initialize?.(root || this.rowsRoot || document);
      window.cmrpCustomDropdowns?.syncAll?.();
    }

    setupExtraRow(row) {
      if (!row) return;

      this.syncDivisionFieldVisibility(row);

      const division = this.fieldInRow(row, 'division');
      const department = this.fieldInRow(row, 'department');
      const course = this.fieldInRow(row, 'course');
      const section = this.fieldInRow(row, 'section');

      // User-driven cascade: populate* methods reset their own children.
      // Do NOT reset course/section after populate — that wiped auto-selected
      // values and left a premature next row while the UI looked incomplete.
      division?.addEventListener('change', () => {
        this.populateDepartmentOptions(department, division?.value);
        this.updateViewMaterialsButton();
      });

      department?.addEventListener('change', () => {
        this.populateCourseOptions(course, department?.value);
        this.updateViewMaterialsButton();
      });

      course?.addEventListener('change', () => {
        this.populateSectionOptions(section, course?.value);
        this.updateViewMaterialsButton();
      });

      section?.addEventListener('change', () => {
        this.maybeAutoAppendRow(row);
        this.updateViewMaterialsButton();
      });

      // Auto-select the first DDCS field only when it has a single option
      // (same cascade as Course/Section). Multiple options stay on the label.
      this._suppressAutoAppend = true;
      try {
        this.seedFirstDdcsField(row);
      } finally {
        this._suppressAutoAppend = false;
      }
    }

    seedFirstDdcsField(row) {
      const needsDivision = this.showDivision();
      const division = this.fieldInRow(row, 'division');
      const department = this.fieldInRow(row, 'department');

      if (needsDivision) {
        this.populateDivisionOptions(division);
        return;
      }

      this.populateDepartmentOptions(department, '');
    }

    /**
     * Clear Division→Section in place (last/only row). Re-seed singleton
     * auto-selects; that still counts as cleared so Clear stays hidden.
     */
    clearRowFields(row) {
      if (!row) return;

      const division = this.fieldInRow(row, 'division');
      const department = this.fieldInRow(row, 'department');
      const course = this.fieldInRow(row, 'course');
      const section = this.fieldInRow(row, 'section');

      this._suppressAutoAppend = true;
      try {
        this.syncDivisionFieldVisibility(row);
        if (division) this.resetSelect(division, 'Division');
        if (department) this.resetSelect(department, 'Department');
        if (course) this.resetSelect(course, 'Course');
        if (section) this.resetSelect(section, 'Section');

        this.seedFirstDdcsField(row);
        window.cmrpCustomDropdowns?.syncAll?.();
      } finally {
        this._suppressAutoAppend = false;
      }

      this.pruneTrailingEmptyRows();
      this.relabelRows();
      this.updateViewMaterialsButton();
    }

    /**
     * Keep all complete rows + at most one trailing empty row (when Campus/Term ready).
     */
    pruneTrailingEmptyRows() {
      if (!this.hasSharedCampusTerm()) {
        this.resetExtraRows();
        return;
      }
      const rows = this.getRows();
      let lastComplete = -1;
      rows.forEach((row, i) => {
        if (this.isRowComplete(row)) lastComplete = i;
      });
      // Remove extras beyond the first empty after last complete (or keep primary only)
      const keepThrough = lastComplete + 1;
      rows.forEach((row, i) => {
        if (i === 0) return; // never remove primary
        if (i > keepThrough) row.remove();
      });
      window.cmrpCustomDropdowns?.cleanupDetached?.();
    }

    syncLockedTermDisplays() {
      // Campus/Term live on the shared row — no per-row lock readouts.
    }

    resetSelect(select, placeholder) {
      if (!select) return;
      select.innerHTML = `<option value="">${placeholder}</option>`;
      select.disabled = true;
    }

    populateDivisionOptions(select, opts = {}) {
      if (!select) return;
      const autoSelect = opts.autoSelect !== false;
      this.resetSelect(select, 'Division');
      const sidebar = window.courseMaterialsSidebar;
      const campusEntityId = sidebar?.campus_entity_id;
      const divisions = window.courseData?.ddcs_campus_division || [];
      const filtered = divisions.filter(
        d => normalize(d.campus_entity_id) === normalize(campusEntityId)
      );
      if (!filtered.length) return;
      select.disabled = false;
      filtered.forEach(d => {
        const option = document.createElement('option');
        option.value = d.campus_division_id;
        stampOption(option, decodeEntities(d.name));
        select.appendChild(option);
      });
      if (autoSelect && filtered.length === 1) {
        select.value = filtered[0].campus_division_id;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      window.cmrpCustomDropdowns?.syncAll?.();
    }

    populateDepartmentOptions(select, divisionId, opts = {}) {
      if (!select) return;
      const autoSelect = opts.autoSelect !== false;
      this.resetSelect(select, 'Department');
      const sidebar = window.courseMaterialsSidebar;
      const campusEntityId = sidebar?.campus_entity_id;
      const departments = window.courseData?.ddcs_campus_department || [];
      const needsDivision = this.showDivision();
      const filtered = departments.filter(dept => {
        if (normalize(dept.campus_entity_id) !== normalize(campusEntityId)) {
          return false;
        }
        if (needsDivision) {
          return normalize(dept.campus_division_id) === normalize(divisionId);
        }
        return true;
      });

      if (!filtered.length) return;
      select.disabled = false;
      filtered.forEach(dept => {
        const option = document.createElement('option');
        option.value = dept.campus_department_id;
        stampOption(option, decodeEntities(dept.name));
        select.appendChild(option);
      });
      if (autoSelect && filtered.length === 1) {
        select.value = filtered[0].campus_department_id;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      window.cmrpCustomDropdowns?.syncAll?.();
    }

    populateCourseOptions(select, departmentId, opts = {}) {
      if (!select) return;
      const autoSelect = opts.autoSelect !== false;
      this.resetSelect(select, 'Course');
      // Course change owns section population — clear section when course resets
      const row = select.closest('.cmrp-course-row');
      const section = row?.querySelector('select[data-cmrp-field="section"]');
      if (section) this.resetSelect(section, 'Section');

      const sidebar = window.courseMaterialsSidebar;
      const campusEntityId = sidebar?.campus_entity_id;
      const courses = window.courseData?.ddcs_course || [];
      const filtered = courses.filter(
        c =>
          normalize(c.campus_entity_id) === normalize(campusEntityId) &&
          normalize(c.campus_department_id) === normalize(departmentId)
      );
      if (!filtered.length) return;
      select.disabled = false;
      filtered.forEach(c => {
        const option = document.createElement('option');
        option.value = c.course_id;
        stampOption(option, decodeEntities(c.coursenumber));
        select.appendChild(option);
      });
      if (autoSelect && filtered.length === 1) {
        select.value = filtered[0].course_id;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      window.cmrpCustomDropdowns?.syncAll?.();
    }

    populateSectionOptions(select, courseId, opts = {}) {
      if (!select) return;
      const autoSelect = opts.autoSelect !== false;
      this.resetSelect(select, 'Section');
      const sidebar = window.courseMaterialsSidebar;
      const campusEntityId = sidebar?.campus_entity_id;
      const termId = sidebar?.term_id || this.sharedCampusTerm().termId;
      const sections = window.courseData?.ddcs_section || [];
      // Match `dev`: only sections for the selected course_id.
      const filtered = sections.filter(
        s =>
          normalize(s.campus_entity_id) === normalize(campusEntityId) &&
          normalize(s.term_id) === normalize(termId) &&
          normalize(s.course_id) === normalize(courseId)
      );
      if (!filtered.length) return;
      select.disabled = false;
      filtered.forEach(s => {
        const option = document.createElement('option');
        option.setAttribute('sectionGid', s.gid);
        const sectionLabel = decodeEntities(s.name);
        option.value = sectionLabel;
        stampOption(option, sectionLabel);
        select.appendChild(option);
      });
      if (autoSelect && filtered.length === 1) {
        select.value = decodeEntities(filtered[0].name);
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      window.cmrpCustomDropdowns?.syncAll?.();
    }

    resetExtraRows() {
      if (!this.rowsRoot) return;
      this.getRows().forEach(row => {
        if (row.getAttribute('data-cmrp-row') === 'primary') return;
        row.remove();
      });
      window.cmrpCustomDropdowns?.cleanupDetached?.();
      this.relabelRows();
      this.updateAddCourseButton();
    }

    /**
     * Drop extra rows and leave a single empty primary line after successful submit.
     * Campus/Term stay as selected (sidebar reset handles DDCS refill).
     */
    async resetAfterSubmit() {
      this.resetExtraRows();
      this.updateViewMaterialsButton();
      this.updateAddCourseButton();
    }

    collectCompleteCourseObjects() {
      const sidebar = window.courseMaterialsSidebar;
      if (!sidebar) return [];

      const { campus, termId, termLabel } = this.sharedCampusTerm();
      const courses = [];

      this.getRows().forEach(row => {
        if (!this.isRowComplete(row)) return;

        const divisionEl = this.fieldInRow(row, 'division');
        const departmentEl = this.fieldInRow(row, 'department');
        const courseEl = this.fieldInRow(row, 'course');
        const sectionEl = this.fieldInRow(row, 'section');

        const sectionOpt = sectionEl?.options?.[sectionEl.selectedIndex];
        const sectionGid = sectionOpt?.getAttribute('sectiongid') || '';

        let division = fullLabel(divisionEl);
        if (!division || division === 'Division') division = '';

        let department = fullLabel(departmentEl);
        if (!department || department === 'Department') department = '';

        let course = fullLabel(courseEl);
        if (!course || course === 'Course') course = '';

        const courseData = {
          selectedDepartment: department,
          selectedCampus: campus,
          selectedTerm: termLabel,
          selectedcourse: course,
          selectedsection: sectionEl?.value || '',
          selectedsectionGid: sectionGid,
          selectedDivision: division,
        };

        // Prefer sidebar helper so termId resolution stays consistent
        const obj =
          typeof sidebar.createCourseObject === 'function'
            ? sidebar.createCourseObject(courseData)
            : {
                ...courseData,
                selectedTermId: termId,
              };

        if (!obj.selectedTermId && termId) {
          obj.selectedTermId = termId;
        }

        courses.push({
          obj,
          department,
          course,
          sectionValue: sectionEl?.value || '',
          sectionText: fullLabel(sectionEl) || sectionEl?.value || '',
          division,
        });
      });

      return courses;
    }

    async handleViewMaterials() {
      const warningId = cmrpId('course-builder-error-warning', 'cb');
      if (typeof window.hideWarningMessage === 'function') {
        window.hideWarningMessage(warningId);
      }

      const complete = this.collectCompleteCourseObjects();
      if (!complete.length) {
        const msg =
          'Please complete at least one course (Campus, Term, Department, Course, and Section).';
        if (
          window.courseMaterialsPageInstance?.showErrorMessage
        ) {
          window.courseMaterialsPageInstance.showErrorMessage(msg, '', false, {
            warningIds: [warningId],
          });
        } else {
          alert(msg);
        }
        return;
      }

      const sidebar = window.courseMaterialsSidebar;
      if (!sidebar) return;

      this.toggleViewMaterialsLoading(true);

      try {
        if (typeof window.hideWarningMessage === 'function') {
          window.hideWarningMessage(cmrpId('student-id-error-warning', 'sid'));
          window.hideWarningMessage('mobile-student-id-error-warning');
        }

        let addedAny = false;

        for (const item of complete) {
          // Skip exact duplicates already in selectedFilters
          const filters =
            sidebar.config?.selectedFilters ||
            window.courseMaterialsPageInstance?.selectedFilters ||
            [];
          const dup = filters.some(
            f =>
              (f.selectedDivision || '') === (item.obj.selectedDivision || '') &&
              f.selectedDepartment === item.obj.selectedDepartment &&
              f.selectedcourse === item.obj.selectedcourse &&
              f.selectedsection === item.obj.selectedsection &&
              f.selectedTermId === item.obj.selectedTermId &&
              f.selectedCampus === item.obj.selectedCampus
          );
          if (dup) continue;

          if (item.department && item.course && item.sectionValue) {
            sidebar.addSelectedCourse?.(
              item.department,
              item.course,
              item.sectionValue,
              item.sectionText || item.sectionValue,
              item.division
            );
          }

          document.dispatchEvent(
            new CustomEvent('sidebar:courseAdded', {
              detail: {
                course: item.obj,
                suppressRefresh: true,
                suppressUrlUpdate: true,
              },
            })
          );
          addedAny = true;
        }

        if (!addedAny) {
          // Deep-link hydrate already filled selectedFilters; if the grid never painted,
          // recover by refreshing instead of treating this as a user duplicate.
          const grid = document.getElementById('filtered-products');
          const hasRenderedResults = Boolean(
            grid?.querySelector?.(
              '.course-card, .course-section, .materials-section, .no-materials-message, .adoption-message-content'
            )
          );
          if (!hasRenderedResults && typeof window.refreshProductsDisplay === 'function') {
            await window.refreshProductsDisplay();
            sidebar.finishCampusTermEdit?.();
            await sidebar.resetCourseBuilderLineKeepCampusTerm?.();
            await this.resetAfterSubmit();
            sidebar.resetCmrpStudentRowAfterCourseBuilderSubmit?.();
            return;
          }
          const msg =
            'Your course selection is already being displayed. Please enter new values.';
          if (window.courseMaterialsPageInstance?.showErrorMessage) {
            window.courseMaterialsPageInstance.showErrorMessage(
              msg,
              '',
              false,
              { warningIds: [warningId] }
            );
          } else {
            alert(msg);
          }
          return;
        }

        window.CmrpSessionSnapshot?.adoptIfForeignFlow?.();
        window.courseMaterialsPageInstance?.components?.urlManager?.setURLSearchParams?.();

        sidebar.finishCampusTermEdit?.({ skipCampusRepopulate: true });

        if (typeof window.refreshProductsDisplay === 'function') {
          await window.refreshProductsDisplay();
        }

        await sidebar.resetCourseBuilderLineKeepCampusTerm?.();
        await this.resetAfterSubmit();
        sidebar.resetCmrpStudentRowAfterCourseBuilderSubmit?.();

        if (
          window.innerWidth <= 968 &&
          !document.body.classList.contains('course-materials-page')
        ) {
          // legacy non-results mobile drawer
        }
      } finally {
        this.toggleViewMaterialsLoading(false);
        this.updateViewMaterialsButton();
      }
    }

    toggleViewMaterialsLoading(isLoading) {
      if (!this.viewBtn) return;
      const textEl =
        this.viewBtn.querySelector(
          '.buy-by-course-builder__view-materials__text'
        ) || this.viewBtn;
      if (isLoading) {
        if (!this.viewBtn.dataset.originalText) {
          this.viewBtn.dataset.originalText = (textEl.textContent || '').trim();
        }
        this.viewBtn.disabled = true;
        textEl.textContent = 'LOADING...';
      } else {
        if (this.viewBtn.dataset.originalText) {
          textEl.textContent = this.viewBtn.dataset.originalText;
          delete this.viewBtn.dataset.originalText;
        }
        this.updateViewMaterialsButton();
      }
    }
  }

  const manager = new CmrpCourseRowManager();

  function boot() {
    manager.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-init after sidebar binds (page may hydrate late)
  window.addEventListener('load', () => {
    manager.init();
  });

  window.CmrpCourseRowManager = manager;
})(window);
